import type { ClientEnvironment } from "@/features/clientEnv";

const WAVE_PAYMENT_HOST = "pay.wave.com";

export type PaymentUrlOpenMode =
  | "iframe"
  | "external_tab"
  | "host_navigation";

interface PaymentUrlOpenContext {
  bucket: ClientEnvironment["bucket"];
  ua: Pick<ClientEnvironment["ua"], "isAndroid">;
}

const isWavePaymentHost = (hostname: string): boolean => {
  const normalizedHost = hostname.toLowerCase();

  return (
    normalizedHost === WAVE_PAYMENT_HOST ||
    normalizedHost.endsWith(`.${WAVE_PAYMENT_HOST}`)
  );
};

const shouldUseHostNavigation = ({
  bucket,
  ua,
}: PaymentUrlOpenContext): boolean => {
  return (
    ua.isAndroid &&
    (bucket === "native_webview" || bucket === "flutter_webview")
  );
};

export const getPaymentUrlOpenMode = (
  url?: string | null,
  countryCode?: string | null,
  context?: PaymentUrlOpenContext
): PaymentUrlOpenMode => {
  const trimmedUrl = url?.trim();

  if (!trimmedUrl) {
    return "iframe";
  }

  if ((countryCode || "").toLowerCase() !== "ci") {
    return "iframe";
  }

  try {
    const parsedUrl = new URL(trimmedUrl);
    if (!isWavePaymentHost(parsedUrl.hostname)) {
      return "iframe";
    }

    if (
      context &&
      shouldUseHostNavigation({
        bucket: context.bucket,
        ua: context.ua,
      })
    ) {
      return "host_navigation";
    }

    return "external_tab";
  } catch (error) {
    return "iframe";
  }
};

export const shouldOpenPaymentUrlInIframe = (
  url?: string | null,
  countryCode?: string | null
): boolean => {
  return getPaymentUrlOpenMode(url, countryCode) === "iframe";
};
